import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';
import { legalEntityKeys, providerKeys } from './useLegalEntities';

/**
 * Proveedores de materiales (Stock y compras): solo entidades con `is_material_provider`.
 * Finanzas usa su propia lista (`is_provider`).
 * @param {Object} [options] Opciones de TanStack Query.
 * @returns {Object} Query con la lista de proveedores.
 */
export const useProviders = (options = {}) => {
  return useQuery({
    queryKey: providerKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get('legal_entities/material-providers');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

/**
 * Proveedores de materiales para el ABM "Stock y compras | Proveedores". Exige el módulo
 * "Proveedores de materiales" (o administrador); los autocompletes siguen usando `useProviders`.
 * @param {Object} [options] Opciones de TanStack Query.
 * @returns {Object} Query con la lista de proveedores.
 */
export const useManagedProviders = (options = {}) => {
  return useQuery({
    queryKey: providerKeys.management(),
    queryFn: async () => {
      const response = await apiClient.get('legal_entities/material-providers/management');
      return response.data;
    },
    ...options,
  });
};

const invalidateProviders = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: providerKeys.all });
  queryClient.invalidateQueries({ queryKey: legalEntityKeys.all });
};

/**
 * Alta o edición de un proveedor de materiales; el backend lo marca como proveedor.
 * @returns {Object} Mutación que recibe `{ id?, data }`.
 */
export const useSaveProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = id
        ? await apiClient.put(`legal_entities/material-providers/${id}`, data)
        : await apiClient.post('legal_entities/material-providers', data);
      return response.data;
    },
    onSuccess: () => invalidateProviders(queryClient),
  });
};

/**
 * Quita la entidad de los proveedores de materiales sin borrarla (Finanzas no cambia).
 * @returns {Object} Mutación que recibe el UUID del proveedor.
 */
export const useRemoveProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`legal_entities/material-providers/${id}`);
      return response.data;
    },
    onSuccess: () => invalidateProviders(queryClient),
  });
};
